import React, { Fragment, useCallback } from "react";
import { useSelector } from "react-redux";
import { duressService } from "client-app-core";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Button,
	IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Translate } from "orion-components/i18n";

// Constants
const UNKNOWN_WORKSTATION = "Unknown Workstation";
const UNKNOWN_USER = "Unknown User";

// Styles moved outside component to prevent recreation
const styles = {
	title: {
		color: "#fff",
		letterSpacing: "unset",
		lineHeight: "unset"
	},
	paperPropStyles: {
		backgroundColor: "#212327",
		padding: "15px",
		alignItems: "center",
		width: "374px"
	},
	closeButton: {
		color: "#fff",
		position: "absolute",
		right: 8,
		top: 8
	},
	description: {
		color: "#D3615C",
		textAlign: "center"
	},
	okButton: {
		height: 23,
		minWidth: 0,
		color: "#fff"
	}
};

// Utility function moved outside component
const stopPropagation = (e) => {
	e.stopPropagation();
};

const DuressNotification = () => {
	// Memoized selectors for better performance
	const shouldNotify = useSelector(
		useCallback((state) => {
			const duressStatus = state.duressStatus;
			if (!duressStatus?.active) return false;

			const { notifyWorkstations } = duressStatus;
			const workstationId = state.session?.identity?.workstationId;

			return duressStatus.isNotifiedWorkstation && notifyWorkstations?.[workstationId]?.ack === false;
		}, [])
	);

	const activatedByWorkstationName = useSelector(
		useCallback((state) => state.duressStatus?.activatedByWorkstationName || UNKNOWN_WORKSTATION, [])
	);

	const activatedByUserName = useSelector(
		useCallback((state) => state.duressStatus?.activatedByUserName || UNKNOWN_USER, [])
	);

	// Memoized event handler
	const handleAcknowledgeDuress = useCallback(() => {
		duressService.ackDuressAlarm((error, response) => {
			if (error) {
				console.error("Failed to acknowledge duress alarm - Error:", error);
				return;
			}

			if (response?.success === false) {
				console.error("Failed to acknowledge duress alarm - Response:", response);
			}
		});
	}, []);

	// Early return if notification shouldn't be shown
	if (!shouldNotify) {
		return null;
	}

	return (
		<Dialog
			open={shouldNotify}
			scroll="paper"
			onMouseDown={stopPropagation}
			onTouchStart={stopPropagation}
			onClose={handleAcknowledgeDuress}
			PaperProps={{
				sx: styles.paperPropStyles
			}}
			aria-labelledby="duress-notification-title"
			aria-describedby="duress-notification-description"
			disableEscapeKeyDown={false}
		>
			<IconButton
				sx={styles.closeButton}
				onClick={handleAcknowledgeDuress}
				aria-label="Close duress notification"
			>
				<CloseIcon />
			</IconButton>

			<DialogTitle id="duress-notification-title" sx={styles.title} component="h2">
				<Translate value="global.duress.notification.title" />
			</DialogTitle>

			<DialogContent>
				<DialogContentText id="duress-notification-description" variant="body2" sx={{ color: "#fff" }}>
					<div style={styles.description}>
						<Translate
							value="global.duress.notification.desc"
							primaryValue={activatedByUserName}
							secondaryValue={activatedByWorkstationName}
						/>
					</div>
				</DialogContentText>
			</DialogContent>

			<DialogActions>
				<Button
					sx={styles.okButton}
					onClick={handleAcknowledgeDuress}
					variant="contained"
					color="primary"
					autoFocus
				>
					<Translate value="global.duress.notification.ok" />
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DuressNotification;
